import { ValueFormatterParams } from "ag-grid-enterprise";

export function decimalFormatter(params: ValueFormatterParams, decimals = 2) {
  // Group rows without an aggFunc (and other blanks) have no value; pass them
  // through so they render empty instead of "NaN".
  if (params.value === null || params.value === undefined) {
    return params.value;
  }
  const numeric = Number(params.value);
  return Number.isNaN(numeric) ? params.value : numeric.toFixed(decimals);
}

/**
 * Formats a decimal fraction as a percentage, e.g. `0.6054` → `"60.54 %"`.
 * The value stays a fraction on the wire and in the grid model (filters,
 * sorting, edits all operate on the fraction) — only the label is scaled.
 */
export function formatPercent(value: number, decimals = 2) {
  return `${(value * 100).toFixed(decimals)} %`;
}

export function percentFormatter(params: ValueFormatterParams, decimals = 2) {
  if (params.value === null || params.value === undefined) {
    return params.value;
  }
  const numeric = Number(params.value);
  if (Number.isNaN(numeric)) {
    return blankIfNaNValue(params.value);
  }
  return formatPercent(numeric, decimals);
}

/**
 * Formats a value that's already percent-scaled (15 meaning 15%, not the
 * 0.15 fraction `formatPercent` expects) as a percent label, e.g. `15` →
 * `"15 %"`. For backend fields whose contract is a 0-100 number (markdown/
 * promotion percentages) rather than a 0-1 fraction (margins).
 *
 * Default matches `PERCENT_UNIT_FILTER_PRECISION` (getColumnFiltersByType) —
 * the finest any percentUnit column is meant to display. A coarser default
 * here would truncate a fractional value on a copy/paste round-trip for any
 * caller (including a future one) that forgets to pass `decimals` explicitly.
 */
export function percentUnitFormatter(
  params: ValueFormatterParams,
  decimals = 2
) {
  if (params.value === null || params.value === undefined) {
    return params.value;
  }
  const numeric = Number(params.value);
  if (Number.isNaN(numeric)) {
    return blankIfNaNValue(params.value);
  }
  return `${numeric.toFixed(decimals)} %`;
}

// A literal NaN cell (an underivable ratio, e.g. a margin over zero cost) has
// no meaningful percent label — blank it. Non-numeric text still passes
// through so the grid keeps showing whatever is actually there.
function blankIfNaNValue(value: unknown) {
  return typeof value === "number" ? "" : value;
}
